import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Trash2, Eye, Calendar, Mail, Phone, DollarSign, Key, ArrowRightLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPatients = () => {
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [resetPasswordModal, setResetPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [transferConfirm, setTransferConfirm] = useState(null);

  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isMainAdmin = adminUser.role !== 'pedo';

  useEffect(() => {
    fetchPatients();
    if (isMainAdmin) fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API}/admin/doctors`, { headers: { Authorization: `Bearer ${token}` } });
      const pedoDoctors = res.data.filter(d => d.role === 'pedo');
      setDoctors(pedoDoctors);
    } catch (err) {
      console.error('Failed to fetch doctors:', err);
      setDoctors([]);
    }
  };

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/patients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatients(response.data);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (patientId, patientName) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/patients/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Patient ${patientName} deleted successfully`);
      setPatients(patients.filter(p => p.id !== patientId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete patient:', error);
      toast.error('Failed to delete patient');
    }
  };

  const handleImpersonate = async (patientId, patientName) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/admin/impersonate/${patientId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const originalToken = localStorage.getItem('adminToken');
      const originalUser = localStorage.getItem('adminUser');
      localStorage.setItem('impersonateOriginalToken', originalToken);
      localStorage.setItem('impersonateOriginalUser', originalUser);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.patient));
      toast.success(`Now viewing as ${patientName}`);
      window.location.href = '/portal/dashboard';
    } catch (error) {
      console.error('Failed to impersonate patient:', error);
      toast.error('Failed to impersonate patient');
    }
  };

  const handleTransfer = async (patientId, doctorId, doctorName) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/patients/${patientId}/transfer`, { doctor_id: doctorId }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Patient transferred to ${doctorName}`);
      fetchPatients();
    } catch (err) {
      console.error('Transfer failed:', err);
      toast.error(err?.response?.data?.detail || 'Failed to transfer patient');
    }
  };

  const handleUnassign = async (patientId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/patients/${patientId}/unassign`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Patient unassigned');
      fetchPatients();
    } catch { toast.error('Failed to unassign patient'); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.put(
        `${API}/admin/patients/${resetPasswordModal.id}/reset-password`,
        { password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Password reset! New password: ${response.data.new_password}`, { duration: 10000 });
      setResetPasswordModal(null);
      setNewPassword('');
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error('Failed to reset password');
    }
  };

  const filteredPatients = patients.filter(patient =>
    patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.phone.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-patients-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          Patient Management
        </h1>
        <p className="text-[#6B7280]">View, manage, and impersonate patients</p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]"
            data-testid="patient-search-input"
          />
        </div>
      </div>

      {/* Patients Table */}
      {filteredPatients.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center" data-testid="no-patients">
          <p className="text-[#6B7280]">
            {searchTerm ? 'No patients found matching your search' : 'No patients registered yet'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="patients-table">
              <thead className="bg-[#F9FAFB] border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">Patient</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">Contact</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">Registered</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-[#1F2937]">Treatments</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-[#1F2937]">Total Paid</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-[#1F2937]">Outstanding</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-[#1F2937]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredPatients.map((patient, index) => (
                  <tr key={patient.id} className="hover:bg-[#F9FAFB] transition-colors" data-testid={`patient-row-${index}`}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-semibold text-[#1F2937]">{patient.name}</div>
                        {patient.date_of_birth && (
                          <div className="text-sm text-[#6B7280]">DOB: {patient.date_of_birth}</div>
                        )}
                        {isMainAdmin && patient.assigned_doctor && (
                          <span className="inline-block mt-1 text-xs font-semibold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full" data-testid={`assigned-badge-${index}`}>
                            Transferred to Dr. Hanadi
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center text-sm text-[#6B7280]">
                          <Mail className="w-4 h-4 mr-2" />
                          {patient.email}
                        </div>
                        <div className="flex items-center text-sm text-[#6B7280]">
                          <Phone className="w-4 h-4 mr-2" />
                          {patient.phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-[#6B7280]">
                        <Calendar className="w-4 h-4 mr-2" />
                        {new Date(patient.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-[#0F4C5C]/10 rounded-full text-sm font-semibold text-[#0F4C5C]">
                        {patient.total_treatments}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm font-semibold text-green-600">
                        ${patient.total_paid.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className={`text-sm font-semibold ${patient.outstanding_balance > 0 ? 'text-orange-600' : 'text-[#6B7280]'}`}>
                        ${patient.outstanding_balance.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center space-x-2">
                        {isMainAdmin && (
                          <button
                            onClick={async () => {
                              let docs = doctors;
                              if (docs.length === 0) {
                                try {
                                  const token = localStorage.getItem('adminToken');
                                  const res = await axios.get(`${API}/admin/doctors`, { headers: { Authorization: `Bearer ${token}` } });
                                  docs = res.data.filter(d => d.role === 'pedo');
                                  setDoctors(docs);
                                } catch (err) {
                                  toast.error('Failed to load doctors');
                                  return;
                                }
                              }
                              if (docs.length > 0) {
                                setTransferConfirm({ patientId: patient.id, patientName: patient.name, doctorId: docs[0].id, doctorName: docs[0].name });
                              } else {
                                toast.error('No pedo doctors available');
                              }
                            }}
                            className={`p-2 rounded-lg transition-colors ${patient.assigned_doctor ? 'text-purple-600 bg-purple-50' : 'text-purple-600 hover:bg-purple-50'}`}
                            title={patient.assigned_doctor ? 'Transferred to Dr. Hanadi - Click to re-transfer' : 'Transfer to Dr. Hanadi'}
                            data-testid={`transfer-${index}`}
                          >
                            <ArrowRightLeft className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleImpersonate(patient.id, patient.name)}
                          className="p-2 text-[#0F4C5C] hover:bg-[#0F4C5C]/10 rounded-lg transition-colors"
                          title="View as Patient"
                          data-testid={`impersonate-${index}`}
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setResetPasswordModal(patient)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Reset Password"
                          data-testid={`reset-password-${index}`}
                        >
                          <Key className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(patient)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Patient"
                          data-testid={`delete-${index}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="delete-confirmation-modal">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Delete Patient
            </h3>
            <p className="text-[#6B7280] mb-6">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This will permanently remove:
            </p>
            <ul className="text-sm text-[#6B7280] mb-6 space-y-2">
              <li>• Patient account and personal information</li>
              <li>• All treatment records ({deleteConfirm.total_treatments} treatments)</li>
              <li>• All payment records</li>
              <li>• All appointments</li>
            </ul>
            <p className="text-red-600 text-sm font-semibold mb-6">
              This action cannot be undone!
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-6 py-3 border-2 border-[#0F4C5C] text-[#0F4C5C] rounded-full font-semibold hover:bg-[#0F4C5C]/5 transition-colors"
                data-testid="delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id, deleteConfirm.name)}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-full font-semibold hover:bg-red-700 transition-colors"
                data-testid="delete-confirm"
              >
                Delete Patient
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="reset-password-modal">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Reset Password
            </h3>
            <p className="text-[#6B7280] mb-6">
              Set a new password for <strong>{resetPasswordModal.name}</strong>
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1F2937] mb-2">
                New Password
              </label>
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]"
                data-testid="new-password-input"
              />
              <p className="text-xs text-[#6B7280] mt-2">
                💡 Tip: Make it easy to remember (e.g., "clinic123", "younes2024")
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Important:</strong> Write down this password! You'll see it in the success message and should share it with the patient.
              </p>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => { setResetPasswordModal(null); setNewPassword(''); }}
                className="flex-1 px-6 py-3 border-2 border-[#0F4C5C] text-[#0F4C5C] rounded-full font-semibold hover:bg-[#0F4C5C]/5 transition-colors"
                data-testid="reset-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                className="flex-1 px-6 py-3 bg-[#0F4C5C] text-white rounded-full font-semibold hover:bg-[#0F4C5C]/90 transition-colors"
                data-testid="reset-confirm"
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Confirmation Modal */}
      {transferConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="transfer-confirmation-modal">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Transfer Patient
            </h3>
            <p className="text-[#6B7280] mb-6">
              Are you sure you want to transfer <strong>{transferConfirm.patientName}</strong> to <strong>{transferConfirm.doctorName}</strong>?
            </p>
            <p className="text-sm text-purple-700 bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
              The patient's records (treatments, payments, appointments, scans, tests, and medical history) will become visible to {transferConfirm.doctorName}.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => setTransferConfirm(null)}
                className="flex-1 px-6 py-3 border-2 border-[#0F4C5C] text-[#0F4C5C] rounded-full font-semibold hover:bg-[#0F4C5C]/5 transition-colors"
                data-testid="transfer-cancel"
              >
                Cancel
              </button>
              <button
                onClick={async () => { await handleTransfer(transferConfirm.patientId, transferConfirm.doctorId, transferConfirm.doctorName); setTransferConfirm(null); }}
                className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-full font-semibold hover:bg-purple-700 transition-colors"
                data-testid="transfer-confirm"
              >
                Yes, Transfer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPatients;
